import { ClassValue, tv, type VariantProps } from "tailwind-variants";
import { SlotsToClasses } from "@nextui-org/theme";
import { EventFor } from "@/utils/types";

export default function Button({
  children, label,
  className, classNames,
  onClick,
  ...tvProps
}: ButtonProps) {
  const style = button(tvProps);
  const showDisabled = Boolean(tvProps.isDisabled || tvProps.isLoading)
  return (
    <button
      data-disabled={showDisabled}
      disabled={showDisabled}
      className={style.base({ className: [className, classNames?.base] })}
      onClick={tvProps.isDisabled ? undefined : onClick}
    >
      {label ?? children}
    </button>
  );
}

const button = tv({
  slots: {
    base: [
      "flex justify-center items-center",
      "rounded-small",
      "font-sans2",
      "cursor-pointer",
    ],
  },
  variants: {
    size: {
      sm: {
        base: "w-24 p-2 text-sm",
      },
      md: {
        base: "w-28 p-3",
      },
      lg: {
        base: "w-36 p-4",
      },
    },
    color: {
      primary: {},
    },
    variant: {
      solid: {},
      clear: {},
    },
    isDisabled: {
      true: { base: "opacity-disabled cursor-default" },
      false: {},
    },
    isLoading: {
      true: {},
      false: {},
    },
    disableHoverShadow: {
      true: {},
      false: {},
    }
  },
  defaultVariants: {
    color: "primary",
    variant: "solid",
    size: 'md',
    isDisabled: false,
    isLoading: false,
    disableHoverShadow: false,
  },
  compoundVariants: [
    {
      color: 'primary',
      variant: 'solid',
      className: {
        base: [
          "bg-primary-600 text-primary-foreground [text-shadow:_1px_2px_1px_#10334A]",
          "data-[disabled=false]:active:bg-primary-700",
        ]
      },
    },
    {
      color: 'primary',
      variant: 'solid',
      disableHoverShadow: false,
      className: {
        base: [
          "hover:[box-shadow:inset_0px_-2px_0px_#4A4A4A]",
        ]
      },
    },
    {
      color: 'primary',
      variant: 'clear',
      className: {
        base: ["text-primary-600 [text-shadow:_0px_1px_1px_#cacaca]"]
      },
    },
  ],
});

type ButtonTVProps = VariantProps<typeof button>;
type CustomButtonProps = {
  /** will override the children */
  label?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: (e: EventFor<"button", "onClick">) => void;
  className?: ClassValue;
  classNames?: SlotsToClasses<keyof ReturnType<typeof button>>;
}

export interface ButtonProps extends ButtonTVProps, CustomButtonProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonTVProps | keyof CustomButtonProps> {
}
